import React from 'react';

export const ComprehensivePrivacyPolicy: React.FC = () => {
    return (
        <div className="max-w-4xl mx-auto text-gray-300">
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
                <p className="text-yellow-400 font-semibold">Aureus Alliance Holdings (Pty) Ltd</p>
                <div className="text-sm text-white mt-2">
                    Last updated: December 2024 | Version 2.0
                </div>
            </div>
            
            <div className="space-y-6 leading-relaxed">
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
                    <h3 className="text-blue-400 font-bold mb-2">🔒 POPIA Compliance Statement</h3>
                    <p className="text-sm">
                        This Privacy Policy complies with the Protection of Personal Information Act (POPIA) of South Africa, 
                        GDPR principles, and international data protection standards. We are committed to protecting your privacy 
                        and handling your personal information responsibly.
                    </p>
                </div>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">1. COMPANY INFORMATION</h2>
                    <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
                        <p><strong className="text-white">Data Controller:</strong> Aureus Alliance Holdings (Pty) Ltd</p>
                        <p><strong className="text-white">Registration Number:</strong> 2025/368711/07</p>
                        <p><strong className="text-white">Address:</strong> 1848 Mees Avenue, Randpark Ridge, Randburg, Gauteng, 2169</p>
                        <p><strong className="text-white">Contact:</strong> privacy@aureus.africa</p>
                        <p><strong className="text-white">Website:</strong> aureus.africa</p>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">2. INFORMATION WE COLLECT</h2>
                    
                    <div className="space-y-4">
                        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                            <h3 className="text-green-400 font-semibold mb-2">📋 Personal Information</h3>
                            <ul className="list-disc list-inside space-y-1 text-sm">
                                <li><strong>Identity Information:</strong> Full name, ID number, date of birth, nationality</li>
                                <li><strong>Contact Information:</strong> Email address, phone number, physical address</li>
                                <li><strong>Financial Information:</strong> Banking details, payment information, transaction history</li>
                                <li><strong>KYC Documentation:</strong> Identity documents, proof of address, facial recognition data</li>
                                <li><strong>Share Ownership:</strong> Purchase history, certificate details, dividend information</li>
                                <li><strong>Communication:</strong> Support tickets, email correspondence, chat logs</li>
                            </ul>
                        </div>

                        <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                            <h3 className="text-purple-400 font-semibold mb-2">💻 Technical Information</h3>
                            <ul className="list-disc list-inside space-y-1 text-sm">
                                <li><strong>Device Data:</strong> IP address, browser type, operating system, device identifiers</li>
                                <li><strong>Usage Analytics:</strong> Pages visited, time spent, click patterns, user journey</li>
                                <li><strong>Cookies:</strong> Session cookies, preference cookies, analytics cookies</li>
                                <li><strong>Security Logs:</strong> Login attempts, security events, access patterns</li>
                                <li><strong>Performance Data:</strong> Page load times, error logs, system performance metrics</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">3. HOW WE USE YOUR INFORMATION</h2>
                    
                    <div className="space-y-3">
                        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                            <h3 className="text-blue-400 font-semibold mb-2">🏢 Business Operations</h3>
                            <ul className="list-disc list-inside space-y-1 text-sm">
                                <li>Process share purchases and maintain accurate shareholder records</li>
                                <li>Issue share certificates through CIPC (10-14 business days)</li>
                                <li>Calculate and distribute dividends to shareholders</li>
                                <li>Manage affiliate and referral programs</li>
                                <li>Provide customer support and resolve inquiries</li>
                                <li>Maintain platform security and prevent fraud</li>
                            </ul>
                        </div>

                        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                            <h3 className="text-yellow-400 font-semibold mb-2">⚖️ Legal Compliance</h3>
                            <ul className="list-disc list-inside space-y-1 text-sm">
                                <li>Comply with KYC (Know Your Customer) requirements</li>
                                <li>Meet anti-money laundering (AML) obligations</li>
                                <li>Fulfill tax reporting requirements</li>
                                <li>Respond to legal requests and court orders</li>
                                <li>Maintain records as required by South African law</li>
                                <li>Report to regulatory authorities when necessary</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">4. INFORMATION SHARING</h2>
                    
                    <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
                        <h3 className="text-red-400 font-bold mb-2">🚫 WE DO NOT SELL YOUR DATA</h3>
                        <p className="text-sm">
                            <strong>Aureus Alliance Holdings does not sell, rent, or trade your personal information to third parties for marketing purposes.</strong>
                        </p>
                    </div>

                    <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-yellow-400">We may share information with:</h3>
                        
                        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
                            <h4 className="text-white font-semibold mb-2">🏛️ Regulatory Authorities</h4>
                            <ul className="list-disc list-inside space-y-1 text-sm">
                                <li>CIPC (Companies and Intellectual Property Commission)</li>
                                <li>South African Revenue Service (SARS)</li>
                                <li>Financial Intelligence Centre (FIC)</li>
                                <li>Information Regulator of South Africa</li>
                                <li>Other regulatory bodies as required by law</li>
                            </ul>
                        </div>

                        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
                            <h4 className="text-white font-semibold mb-2">🤝 Service Providers</h4>
                            <ul className="list-disc list-inside space-y-1 text-sm">
                                <li>Payment processors (for transaction processing)</li>
                                <li>KYC verification services</li>
                                <li>Cloud hosting providers (with data protection agreements)</li>
                                <li>Email service providers</li>
                                <li>Customer support platforms</li>
                                <li>Analytics and security services</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">5. DATA SECURITY</h2>
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                        <h3 className="text-green-400 font-semibold mb-2">🔐 Security Measures</h3>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>End-to-end encryption for sensitive data transmission</li>
                            <li>Secure cloud infrastructure with regular security audits</li>
                            <li>Multi-factor authentication for account access</li>
                            <li>Regular security assessments and penetration testing</li>
                            <li>Staff training on data protection and security protocols</li>
                            <li>Incident response procedures for data breaches</li>
                        </ul>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">6. YOUR RIGHTS UNDER POPIA</h2>
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                        <h3 className="text-purple-400 font-semibold mb-2">📋 Data Subject Rights</h3>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                            <li><strong>Right of Access:</strong> Request copies of your personal information</li>
                            <li><strong>Right to Correction:</strong> Update or correct inaccurate information</li>
                            <li><strong>Right to Deletion:</strong> Request deletion of your personal information</li>
                            <li><strong>Right to Object:</strong> Object to processing in certain circumstances</li>
                            <li><strong>Right to Portability:</strong> Receive your data in a structured format</li>
                            <li><strong>Right to Complaint:</strong> Lodge complaints with the Information Regulator</li>
                        </ul>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">7. DATA RETENTION</h2>
                    <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4">
                        <h3 className="text-orange-400 font-semibold mb-2">📅 Retention Periods</h3>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                            <li><strong>Shareholder Records:</strong> Retained for duration of share ownership plus 7 years</li>
                            <li><strong>KYC Documentation:</strong> 5 years after account closure (regulatory requirement)</li>
                            <li><strong>Transaction Records:</strong> 5 years (tax and audit requirements)</li>
                            <li><strong>Communication Records:</strong> 3 years for support and legal purposes</li>
                            <li><strong>Website Analytics:</strong> 2 years for performance optimization</li>
                        </ul>
                    </div>
                </section>

                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-white border-b border-gray-600 pb-2">8. CONTACT INFORMATION</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                            <h3 className="text-blue-400 font-semibold mb-2">📧 Data Protection Officer</h3>
                            <p className="text-sm">
                                <strong>Email:</strong> privacy@aureus.africa<br/>
                                <strong>Phone:</strong> +27 (0)74 449 3251<br/>
                                <strong>Address:</strong> 1848 Mees Avenue, Randpark Ridge, Randburg, Gauteng, 2169<br/>
                                <strong>Response Time:</strong> Within 30 days
                            </p>
                        </div>
                        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                            <h3 className="text-green-400 font-semibold mb-2">🏛️ Information Regulator</h3>
                            <p className="text-sm">
                                If unsatisfied with our response:<br/>
                                <strong>Website:</strong> inforeg.org.za<br/>
                                <strong>Email:</strong> complaints.IR@justice.gov.za<br/>
                                <strong>Phone:</strong> +27 (0)12 406 4818
                            </p>
                        </div>
                    </div>
                </section>

                <div className="border-t border-gray-600 pt-6 mt-8">
                    <p className="text-center text-sm text-white">
                        By using this platform, you acknowledge that you have read, understood, and consent to the processing 
                        of your personal information as described in this Privacy Policy.
                    </p>
                </div>
            </div>
        </div>
    );
};
